import { useQuery, useMutation, useQueryClient, ref } from "react-query";
import { Auth } from "shared/@auth/Auth";
import dayjs from "dayjs";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";

const currentYear = dayjs().format("YYYY");

const yearArray = new Array(3);
for (let i = 0, f = currentYear - 2; i < 3; i++, f++) {
  yearArray[i] = f.toString();
}

export function useSummariesQueries(year) {
  const { makeAuthedRequest } = Auth.useContainer();

  const [meetingType, setMeetingType] = useState("mt");
  const [selectedYear, setSelectedYear] = useState(year);

  const location = useLocation();

  useEffect(() => {
    if (location.pathname.split("/")[1] === "executive-committee") {
      setMeetingType("ec");
    } else {
      setMeetingType("mt");
    }
  }, [location]);

  useEffect(() => {
    setSelectedYear(year);
  }, [year]);

  const { data, status, refetch } = useQuery([meetingType, selectedYear], () =>
    makeAuthedRequest(`/management-team/summaries/${meetingType}/${year}`, {
      method: "GET",
    }).then((res) => res.json())
  );

  return { data, status, refetch };
}

export function useSummariesMutations() {
  const { makeAuthedRequest } = Auth.useContainer();
  const queryClient = useQueryClient();

  const addSummary = async (summary) => {
    return makeAuthedRequest("management-team/summary/add", {
      method: "post",
      body: JSON.stringify(summary),
    });
  };

  const addSummaryMutation = useMutation(addSummary, {
    onSuccess: () => {
      for (let year in yearArray) {
        queryClient.invalidateQueries(["mt", yearArray[year]]);
        queryClient.invalidateQueries(["ec", yearArray[year]]);
      }
      toast.success("Summary added!");
    },
    onError: (err) => {
      console.error(err);
      toast.error("Something went wrong!");
    },
  });

  const publishSummary = async (summary) => {
    return makeAuthedRequest("/management-team/summary/publish", {
      method: "put",
      body: JSON.stringify(summary),
    });
  };

  const publishSummaryMutation = useMutation(publishSummary, {
    onSuccess: (data) => {
      toast.success(data);
    },
    onError: (err) => {
      console.error(err);
      toast.error("Something went wrong!");
    },
  });

  const editSummary = async (summary) => {
    return makeAuthedRequest("management-team/summary/edit", {
      method: "put",
      body: JSON.stringify(summary),
    });
  };

  const editSummaryMutation = useMutation(editSummary, {
    onSuccess: () => {
      for (let year in yearArray) {
        queryClient.invalidateQueries(["mt", yearArray[year]]);
        queryClient.invalidateQueries(["ec", yearArray[year]]);
      }
    },
    onError: (err) => {
      console.error(err);
    },
  });

  const deleteSummary = async (summary) => {
    return makeAuthedRequest("management-team/summary/delete", {
      method: "delete",
      body: JSON.stringify(summary),
    });
  };

  const deleteSummaryMutation = useMutation(deleteSummary, {
    onSuccess: () => {
      for (let year in yearArray) {
        queryClient.invalidateQueries(["mt", yearArray[year]]);
        queryClient.invalidateQueries(["ec", yearArray[year]]);
      }
    },
    onError: (err) => {
      console.error(err);
    },
  });

  return {
    addSummary: addSummaryMutation,
    publishSummary: publishSummaryMutation,
    editSummary: editSummaryMutation,
    deleteSummary: deleteSummaryMutation,
  };
}

// Backward compatible combined hook
export function useSummaries(year) {
  const q = useSummariesQueries(year);
  const m = useSummariesMutations();
  return {
    ...q,
    addSummary: m.addSummary,
    publishSummary: m.publishSummary,
    editSummary: m.editSummary,
    deleteSummary: m.deleteSummary,
  };
}

// Keep existing individual hooks for compatibility (they return the mutate function)
export function useAddSummary() {
  const { addSummary } = useSummariesMutations();
  return addSummary.mutate;
}

export function usePublishSummary() {
  const { publishSummary } = useSummariesMutations();
  return publishSummary.mutate;
}

export function useEditSummary() {
  const { editSummary } = useSummariesMutations();
  return editSummary.mutate;
}

export function useDeleteSummary() {
  const { deleteSummary } = useSummariesMutations();
  return deleteSummary.mutate;
}
